import { FC, useState } from 'react';

import { useTranslation, Trans } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  typographyVariants,
  CircularLoadingButton,
} from '@mint-lib/tw-components';

import { useAcknowledgeCertificateMutation } from '../../api/cO2Slice.js';
import type { AcknowledgmentModalState } from '../../types/co2notifications.js';

type AcknowledgmentStatus = 'accepted' | 'rejected';

type Props = {
  modalState: AcknowledgmentModalState;
  campaignId: string;
  onClose: () => void;
  refreshOrderList: () => Promise<unknown>;
};

const AcknowledgmentDisclaimerModal: FC<Props> = ({
  modalState,
  campaignId,
  onClose,
  refreshOrderList,
}) => {
  const { t } = useTranslation('@myn/planning');
  const [acknowledgmentStatus, setAcknowledgmentStatus] =
    useState<AcknowledgmentStatus | null>(null);
  const [isPending, setIsPending] = useState(false);

  const [acknowledgeCertificate] = useAcknowledgeCertificateMutation();

  const handleClose = () => {
    setAcknowledgmentStatus(null);
    onClose();
  };

  const handleConfirmation = async () => {
    if (acknowledgmentStatus !== 'accepted' || !modalState.isOpen) {
      handleClose();
      return;
    }

    setIsPending(true);

    try {
      await acknowledgeCertificate({
        campaignCollectionId: Number(campaignId),
      }).unwrap();
      await refreshOrderList();
      globalThis.open(modalState.certificateUrl, '_blank');
      handleClose();
    } catch {
      enqueueSnackbar({
        variant: 'error',
        title: t('Something went wrong'),
        subTitle: t('Please, try again.'),
        textLayout: 'inline',
        dismissible: true,
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog modal onOpenChange={handleClose} open={modalState.isOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="m" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('User Acknowledgment')}</DialogTitle>
          </DialogHeader>
          <DialogContent
            className={typographyVariants({
              variant: 'bodyLong01',
              className: 'grid gap-y-6',
            })}
          >
            <p>
              {t(
                'By accessing this certificate generation process, you acknowledge that any certificate generated through the MINT ARM Platform is intended for general informational and B2B purposes.',
              )}
            </p>
            <p>
              <Trans
                i18nKey="You further acknowledge that such certificate is not intended, in itself, to substantiate environmental claims or sustainability labels directed at consumers or to demonstrate compliance with <NoWrap>Directive (EU) 2024/825</NoWrap> (&ldquo;<Bold>EmpCo</Bold>&ldquo;)."
                t={t}
                components={{
                  NoWrap: <span className="whitespace-nowrap" />,
                  Bold: <span className="font-bold" />,
                }}
              />
            </p>
            <p>
              {t(
                'If you intend to use the certificate, or any information contained therein, in any consumer-facing communication, advertising or environmental claim, you are required to contact MINT in advance to assess its appropriate use.',
              )}
            </p>

            <RadioGroup
              onValueChange={(value: AcknowledgmentStatus) =>
                setAcknowledgmentStatus(value)
              }
            >
              <Label
                className={typographyVariants({
                  variant: 'label01',
                  className: 'flex items-start gap-x-1',
                })}
              >
                <RadioGroupItem className="shrink-0" value="accepted" />
                {t(
                  'I acknowledge that I have read and understood the above notice and the limitations concerning the use of the certificate.',
                )}
              </Label>
              <Label
                className={typographyVariants({
                  variant: 'label01',
                  className: 'flex items-start gap-x-1',
                })}
              >
                <RadioGroupItem className="shrink-0" value="rejected" />
                {t(
                  'I do not acknowledge the above notice and understand that I cannot proceed with the generation of the certificate.',
                )}
              </Label>
            </RadioGroup>
          </DialogContent>
          <DialogFooter>
            <Button
              size="large"
              variant="text"
              color="primary"
              onClick={handleClose}
              aria-label="Close the modal"
              data-dd-action-name="User Acknowledgment modal:close"
            >
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={handleConfirmation}
              aria-label="Confirm the action"
              data-dd-action-name="User Acknowledgment modal:confirm"
              disabled={!acknowledgmentStatus || isPending}
            >
              {isPending && (
                <CircularLoadingButton size="small" variant="contained" />
              )}
              {t('Confirm')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export default AcknowledgmentDisclaimerModal;
